"use client";

/**
 * AssessmentFlow — shown as a modal after clicking "Start" on a DiagnosticCard.
 *
 * Flow:
 *   intro   → user clicks "Start Assessment"
 *   loading → questions are fetched (static or dynamic API), session created in Firestore
 *   → navigates to /diagnostics/saved/${sessionId}/questions
 *
 * There is NO question display here — that happens on the questions page.
 * This component only handles the pre-flight info screen and loading state.
 */
import { useAssessment } from "@/modes/diagnostics/hooks/useAssessment";
import type { DiagnosticTemplate } from "@/modes/diagnostics/data";
import { SenseiMarkLoader } from "@/shared/components/layout/SenseiMarkLoader";

interface Props {
  diagnostic: DiagnosticTemplate;
  onClose:    () => void;
}

export function AssessmentFlow({ diagnostic, onClose }: Props) {
  const { status, error, start } = useAssessment();

  const isLoading = status === "loading";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${diagnostic.title} assessment`}
      style={{
        position:       "fixed",
        inset:          0,
        zIndex:         100,
        background:     "rgba(0,0,0,0.75)",
        display:        "flex",
        alignItems:     "center",
        justifyContent: "center",
        padding:        "24px",
        backdropFilter: "blur(6px)",
      }}
    >
      <div
        style={{
          background:   "var(--card, var(--background))",
          border:       "1px solid var(--border)",
          borderRadius: "16px",
          width:        "100%",
          maxWidth:     "560px",
          padding:      "32px",
          position:     "relative",
          color:        "var(--foreground)",
        }}
      >
        {/* Close */}
        {!isLoading && (
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              position:   "absolute",
              top:        "16px",
              right:      "16px",
              background: "none",
              border:     "none",
              cursor:     "pointer",
              color:      "var(--muted-foreground)",
              fontSize:   "20px",
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        )}

        {/* Loading state */}
        {isLoading ? (
          <div style={{ padding: "16px 0" }}>
            <SenseiMarkLoader size={36} />
            <p style={{ fontSize: "15px", color: "var(--muted-foreground)", margin: 0, textAlign: "center" }}>
              Generating your diagnostic…
            </p>
          </div>
        ) : (
          <div>
            <p style={{ fontSize: "12px", color: "var(--muted-foreground)", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.08em" }}>
              {diagnostic.tag}
            </p>
            <h2 style={{ margin: "0 0 12px", fontSize: "22px", lineHeight: 1.3, color: "var(--foreground)" }}>
              {diagnostic.title}
            </h2>
            <p style={{ margin: "0 0 28px", fontSize: "15px", color: "var(--muted-foreground)", lineHeight: 1.6 }}>
              {diagnostic.desc}
            </p>
            <div style={{ display: "flex", gap: "16px", fontSize: "13px", color: "var(--muted-foreground)", marginBottom: "32px" }}>
              <span>⏱ ~10 minutes</span>
              <span>📊 Scored 1–5</span>
              <span>📄 Full action plan on completion</span>
            </div>

            <button
              onClick={() => start(diagnostic.id)}
              disabled={isLoading}
              style={{
                width:          "100%",
                padding:        "14px",
                background:     "var(--primary, #5B6CFF)",
                border:         "none",
                borderRadius:   "10px",
                color:          "var(--primary-foreground, #fff)",
                fontSize:       "15px",
                fontWeight:     600,
                cursor:         "pointer",
              }}
            >
              Start Assessment
            </button>

            {error && (
              <p style={{ color: "var(--destructive, #ef4444)", fontSize: "13px", marginTop: "12px", textAlign: "center" }}>
                {error}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
